/**
 * Real-data adapter: converts the backend's EntityMappingDraft (every
 * canonical field, N ranked real vendor-field candidates) into the exact
 * MappingField[] shape nodeDefs/useBuilder.ts already render — so the
 * existing FieldRow/MappingTable UI works unchanged, just fed real data
 * instead of the seeds.ts mock scenario.
 */
import type { EntityMappingDraft } from '../api/builder';
import type { Direction, MappingField } from './types';

const TRANSFORM_LABEL: Record<string, string> = {
  direct: 'direct',
  coerce: 'type-coerce',
  merge: 'merge · N→1',
  split: 'split',
  passthrough: 'passthrough',
  default: 'default',
};

/**
 * Build MappingField[] for one direction from the real entity draft.
 * outbound: canonical -> vendor (from = canonical field, to = top vendor candidate)
 * inbound:  vendor -> canonical (from = top vendor candidate, to = canonical field)
 * A field with zero real candidates is marked 'needs' (genuinely unresolved —
 * the AI found nothing plausible), same semantics the mock DISC fields used.
 */
export function fieldsFromEntityDraft(draft: EntityMappingDraft, dir: Direction): MappingField[] {
  return draft.fields.map((f) => {
    const top = f.candidates[0];
    const via = TRANSFORM_LABEL[f.transform] ?? f.transform;
    const confPct = top ? Math.round(top.confidence * 100) : 0;
    const conf = top ? `${confPct}%` : '—';
    const vendorField = top?.vendorField ?? '— unresolved —';
    // Real vendor field type (e.g. HubSpot's "string"/"enumeration"), looked
    // up from the live properties fetch — never guessed. Canonical field type
    // isn't in this draft (that's catalog-dictionary metadata the FE doesn't
    // fetch yet), so it stays blank rather than showing a fabricated value.
    const vendorType = top ? draft.vendorFieldTypes[top.vendorField] ?? '' : '';
    const state: MappingField['state'] = f.candidates.length === 0 ? 'needs' : 'settled';
    // node/disc key every field (not just ambiguous ones, unlike the mock) so
    // every row can expose its real candidate list via the same resolve panel.
    const node = `${dir}:${f.canonicalField}`;

    return dir === 'outbound'
      ? { from: f.canonicalField, fromType: '', to: vendorField, toType: vendorType, via, conf, state, disc: f.canonicalField, node }
      : { from: vendorField, fromType: vendorType, to: f.canonicalField, toType: '', via, conf, state, disc: f.canonicalField, node };
  });
}

/** Ranked candidates for one canonical field, in the shape FieldRow expects. */
export function candidatesFor(
  draft: EntityMappingDraft,
  canonicalField: string,
): Array<{ label: string; conf: string; note: string }> {
  const field = draft.fields.find((f) => f.canonicalField === canonicalField);
  if (!field) return [];
  return field.candidates.map((c) => ({
    label: c.vendorField,
    conf: `${Math.round(c.confidence * 100)}%`,
    note: c.note,
  }));
}
